/**
 * 系统管理写域 repository（/api/sys/* 真实库域）
 * ------------------------------------------------------------
 * 部门 / 岗位 / 菜单 / 字典 / 参数 / 日志 / 在线会话 七组管理接口。
 * 统一响应包由 http-client 解包；失败抛 ApiError（message 可直接展示）。
 * 字段以后端 sysadmin 各 controller/service 实际返回为准。
 */
import { apiGet, apiSend } from './http-client'

/* ---------- AI 工具（聊天只读工具启停） ---------- */
export interface ChatTool {
  name: string; description: string; enabled: boolean
  timeoutMs: number; maxResultBytes: number
  params: { key: string; required: boolean }[]
  callCount: number; lastCalledAt: string | null
}
export interface ChatToolCall {
  id: string; toolName: string; status: string; summary: string | null; errorCode: string | null
  durationMs: number | null; createdAt: string; ownerName: string | null; sessionId: string
}
export const chatToolRepo = {
  list: () => apiGet<ChatTool[]>('/api/sys/tools'),
  toggle: (name: string, enabled: boolean) =>
    apiSend<{ name: string; enabled: boolean }>('POST', `/api/sys/tools/${encodeURIComponent(name)}/toggle`, { enabled }),
  calls: (q: { tool?: string; status?: string; limit?: number }) =>
    apiGet<ChatToolCall[]>('/api/sys/tools/calls', { ...q })
}

/* ---------- 部门 ---------- */
export interface SysDept {
  id: string; name: string; parentId: string | null; path: string; level: number
  sort: number; status: 'active' | 'disabled'; remark: string
  memberCount?: number; children?: SysDept[]
}
export interface DeptTree { items: SysDept[]; total: number }
export const deptRepo = {
  tree: () => apiGet<DeptTree>('/api/sys/depts'),
  create: (body: { name: string; parentId?: string; sort?: number; remark?: string }) =>
    apiSend<SysDept>('POST', '/api/sys/depts', body),
  update: (id: string, body: { name?: string; sort?: number; status?: string; remark?: string; parentId?: string | null }) =>
    apiSend<SysDept>('PUT', `/api/sys/depts/${encodeURIComponent(id)}`, body),
  remove: (id: string) => apiSend<{ id: string; deleted: boolean }>('DELETE', `/api/sys/depts/${encodeURIComponent(id)}`)
}

/* ---------- 岗位 ---------- */
export interface SysPost {
  id: string; code: string; name: string; sort: number
  status: 'active' | 'disabled'; remark: string; memberCount?: number; createdAt: string
}
export interface PostMember { id: string; name: string; deptId: string | null; deptName: string; status: string; email: string; phone: string }
export const postRepo = {
  list: () => apiGet<SysPost[]>('/api/sys/posts'),
  members: (id: string) => apiGet<PostMember[]>(`/api/sys/posts/${encodeURIComponent(id)}/members`),
  create: (body: { code: string; name: string; sort?: number; remark?: string }) =>
    apiSend<SysPost>('POST', '/api/sys/posts', body),
  update: (id: string, body: { name?: string; sort?: number; status?: string; remark?: string }) =>
    apiSend<SysPost>('PUT', `/api/sys/posts/${encodeURIComponent(id)}`, body),
  remove: (id: string) => apiSend<{ id: string; deleted: boolean }>('DELETE', `/api/sys/posts/${encodeURIComponent(id)}`)
}

/* ---------- 菜单 ---------- */
/** 菜单节点挂载的功能项（功能树中同权限分组的子节点，含功能/操作） */
export interface MenuFunctionItem {
  code: string; name: string; kind: string; nodeType: 'dir' | 'func' | 'action'; status: string
}
export interface MenuLeafNode {
  id: string; name: string; route: string | null; stub: boolean | null; tag: string | null
  status: 'active' | 'disabled'; sort: number; description: string
  permissionCode: string | null; appCode: string | null; updatedAt: string
  functions: MenuFunctionItem[]
}
export interface MenuModuleNode {
  code: string; name: string; ic: string; color: string; desc: string
  status: 'active' | 'disabled'; sort: number; permissionCode: string | null
  updatedAt: string; functions: MenuFunctionItem[]; items: MenuLeafNode[]
}
export interface MenuTree { modules: MenuModuleNode[]; total: number; note: string }
export interface CreateMenuBody {
  code: string; name: string; parentCode?: string; path?: string; icon?: string; color?: string
  sort: number; nodeKind: 'module' | 'leaf'; permissionCode?: string; description?: string
  tag?: string; stub?: boolean; appCode?: string
}
export type UpdateMenuBody = Partial<Omit<CreateMenuBody, 'code' | 'nodeKind'>>
export const menuAdminRepo = {
  tree: () => apiGet<MenuTree>('/api/sys/menus'),
  create: (body: CreateMenuBody) => apiSend<{ code: string; nodeKind: string; version: number }>('POST', '/api/sys/menus', body),
  update: (id: string, body: UpdateMenuBody) =>
    apiSend<{ code: string; version: number }>('PUT', `/api/sys/menus/${encodeURIComponent(id)}`, body),
  remove: (id: string) => apiSend<{ code: string; deleted: boolean; version: number }>('DELETE', `/api/sys/menus/${encodeURIComponent(id)}`)
}

/* ---------- 字典 ---------- */
export interface DictType {
  id: string; code: string; name: string; status: 'active' | 'disabled'
  remark: string; dataCount?: number; createdAt: string
}
export interface DictData {
  id: string; typeCode: string; label: string; value: string; sort: number
  cssClass: string; status: 'active' | 'disabled'; remark: string; createdAt: string
}
export const dictRepo = {
  types: () => apiGet<DictType[]>('/api/sys/dict/types'),
  /** 业务下拉公开读（仅需登录）：仅启用类型 + 启用数据行 */
  publicData: (typeCode: string) =>
    apiGet<{ label: string; value: string; sort: number; cssClass: string }[]>(`/api/sys/dict/data/${encodeURIComponent(typeCode)}`),
  createType: (body: { code: string; name: string; remark?: string }) => apiSend<DictType>('POST', '/api/sys/dict/types', body),
  updateType: (id: string, body: { name?: string; status?: string; remark?: string }) =>
    apiSend<DictType>('PUT', `/api/sys/dict/types/${encodeURIComponent(id)}`, body),
  removeType: (id: string) => apiSend<{ id: string; deleted: boolean }>('DELETE', `/api/sys/dict/types/${encodeURIComponent(id)}`),
  data: (typeCode: string) => apiGet<DictData[]>(`/api/sys/dict/types/${encodeURIComponent(typeCode)}/data`),
  createData: (typeCode: string, body: { label: string; value: string; sort?: number; cssClass?: string; remark?: string }) =>
    apiSend<DictData>('POST', `/api/sys/dict/types/${encodeURIComponent(typeCode)}/data`, body),
  updateData: (id: string, body: { label?: string; value?: string; sort?: number; cssClass?: string; status?: string; remark?: string }) =>
    apiSend<DictData>('PUT', `/api/sys/dict/data/${encodeURIComponent(id)}`, body),
  removeData: (id: string) => apiSend<{ id: string; deleted: boolean }>('DELETE', `/api/sys/dict/data/${encodeURIComponent(id)}`)
}

/* ---------- 版本管理（工程版本台账 + 每次提交更新日志；权威在库，与 package.json 无关） ---------- */
export interface VersionModule { moduleCode: string; version: string }
export interface VersionLedgerItem {
  moduleCode: string; label: string; version: string
  versionNo: string; releaseId: string; releaseTitle: string; releasedAt: string
}
export interface VersionRelease {
  id: string; versionNo: string; title: string; changeType: string
  commitSha: string; detail: string; createdBy: string; createdAt: string
  modules: VersionModule[]
}
export const versionRepo = {
  ledger: () => apiGet<VersionLedgerItem[]>('/api/sys/versions/ledger'),
  list: (q?: { module?: string; changeType?: string }) =>
    apiGet<VersionRelease[]>('/api/sys/versions', q && (q.module || q.changeType) ? { ...q } : undefined),
  create: (body: { versionNo: string; title: string; changeType: string; commitSha?: string; detail?: string; modules: VersionModule[] }) =>
    apiSend<VersionRelease>('POST', '/api/sys/versions', body),
  update: (id: string, body: Partial<{ versionNo: string; title: string; changeType: string; commitSha: string; detail: string; modules: VersionModule[] }>) =>
    apiSend<VersionRelease>('PUT', `/api/sys/versions/${encodeURIComponent(id)}`, body),
  remove: (id: string) => apiSend<{ id: string; deleted: boolean }>('DELETE', `/api/sys/versions/${encodeURIComponent(id)}`)
}

/* 版本公开只读（/api/versions，无权限点要求）：品牌浮窗展示版本管理登记的台账与更新日志 */
export const versionPublicRepo = {
  ledger: () => apiGet<VersionLedgerItem[]>('/api/versions/ledger'),
  releases: (module?: string) =>
    apiGet<VersionRelease[]>('/api/versions/releases', module ? { module } : undefined),
}

/* ---------- 参数（key 为主键；kind=system 内置不可删；grp 分组；secret 敏感值加密存储、读取不回显） ---------- */
export interface SysConfig {
  id: string; key: string; name: string; value: string; kind: 'system' | 'custom'
  grp: string; secret: boolean; hasValue: boolean
  remark: string; updatedBy: string; createdAt: string; updatedAt: string
}
export interface SysConfigGroup {
  code: string; name: string; sort: number; remark: string; params: number
}
export const configRepo = {
  list: (grp?: string) => apiGet<SysConfig[]>('/api/sys/configs', grp ? { grp } : undefined),
  groups: () => apiGet<SysConfigGroup[]>('/api/sys/config-groups'),
  renameGroup: (code: string, body: { name?: string; sort?: number; remark?: string }) =>
    apiSend<SysConfigGroup>('PUT', `/api/sys/config-groups/${encodeURIComponent(code)}`, body),
  create: (body: { key: string; name: string; grp: string; grpName?: string; secret?: boolean; value?: string; secretValue?: string; remark?: string }) =>
    apiSend<SysConfig>('POST', '/api/sys/configs', body),
  update: (key: string, body: { name?: string; grp?: string; grpName?: string; value?: string; secretValue?: string; remark?: string }) =>
    apiSend<SysConfig>('PUT', `/api/sys/configs/${encodeURIComponent(key)}`, body),
  remove: (key: string) => apiSend<{ key: string; deleted: boolean }>('DELETE', `/api/sys/configs/${encodeURIComponent(key)}`)
}

/* ---------- 日志 ---------- */
export interface OperLogItem {
  id: number; personId: string; personName: string; module: string; action: string
  method: string; url: string; params: unknown; result: 'success' | 'fail'
  errorMsg: string; ip: string; userAgent: string; costMs: number; createdAt: string
}
export interface LoginLogItem {
  id: number; account: string; personId: string; accountKind: string; identifier: string
  result: string; ip: string; userAgent: string; createdAt: string
}
export interface LogPage<T> { items: T[]; total: number; page: number; size: number }
export const logRepo = {
  oper: (q: { module?: string; result?: string; page?: number; size?: number }) =>
    apiGet<LogPage<OperLogItem>>('/api/sys/logs/oper', { ...q }),
  clearOper: () => apiSend<{ removed: number }>('DELETE', '/api/sys/logs/oper'),
  login: (q: { page?: number; size?: number }) => apiGet<LogPage<LoginLogItem>>('/api/sys/logs/login', { ...q })
}

/* ---------- 在线会话 ---------- */
export interface OnlineSession {
  tokenHash: string; personId: string; personName: string; account: string
  ip: string; userAgent: string; createdAt: string; expiresAt: string
}
export const onlineRepo = {
  list: () => apiGet<OnlineSession[]>('/api/sys/online'),
  kick: (tokenHash: string) => apiSend<{ tokenHash: string; kicked: boolean }>('DELETE', `/api/sys/online/${encodeURIComponent(tokenHash)}`)
}
